'use client'
import {
  Toast_default
} from "./chunk-HT4VCWUT.mjs";
import {
  Modal_default
} from "./chunk-JK4YX6P4.mjs";
import {
  ImageWithFallback_default
} from "./chunk-VPIMEK75.mjs";
import {
  Typography_default
} from "./chunk-TDL5JRE5.mjs";
import {
  Button_default
} from "./chunk-EGOGHW2A.mjs";
import {
  __async
} from "./chunk-BTG5EALA.mjs";

// src/Share/Share.tsx
import { useMemo, useRef } from "react";
import { useDeviceDetect } from "@wbk/hooks";
import { Facebook, LinkedIn, Mail, Whatsapp, WorldIcon, X } from "@wbk/svg";
import { useTranslation as useTranslation2 } from "@wbk/config";

// src/Share/CopyButton.tsx
import { useEffect, useState } from "react";
import { CopyIcon } from "@wbk/svg";
import { useTranslation } from "@wbk/config";
import { jsx, jsxs } from "react/jsx-runtime";
var CopyButton = ({ url, onError }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation("ui_common");
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCopied(false);
    }, 1e3);
    return () => clearTimeout(timeout);
  }, [copied]);
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (error) {
      onError();
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "relative", children: [
    /* @__PURE__ */ jsxs(
      Button_default,
      {
        className: "radius-XS h-12 w-full justify-start gap-3",
        shape: "outlined",
        theme: "white",
        onClick: handleCopy,
        children: [
          /* @__PURE__ */ jsx(CopyIcon, { className: "h-6 w-6 shrink-0" }),
          /* @__PURE__ */ jsx(Typography_default, { variant: "label-M", as: "span", children: t("ui_common:share.copy") })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Typography_default,
      {
        className: `absolute bottom-full left-1/2 -translate-x-1/2 transition-all ${copied ? "opacity-100" : "opacity-0"}`,
        variant: "body-XS",
        children: t("ui_common:share.copied")
      }
    )
  ] });
};
var CopyButton_default = CopyButton;

// src/Share/Share.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ShareModal = ({
  heading,
  render,
  modal = true,
  channels = ["whatsapp", "x", "linkedIn", "email"],
  data,
  poster,
  className,
  icon,
  hideText
}) => {
  const { isMobile } = useDeviceDetect();
  const toast = useRef(null);
  const { t } = useTranslation2("ui_common");
  const url = data.url || (typeof window !== "undefined" ? window.location.href : "");
  const encodedShareUrl = encodeURIComponent(url);
  const encodedShareTitle = encodeURIComponent(data.title || "");
  const encodedShareDescription = encodeURIComponent(data.text || "");
  const shareOptions = useMemo(() => {
    return {
      whatsapp: {
        label: "whatsapp",
        url: `https://${isMobile ? "api" : "web"}.whatsapp.com/send?text=${encodedShareTitle}%20${encodedShareUrl}`,
        icon: /* @__PURE__ */ jsx2(Whatsapp, { className: "h-6 w-6 shrink-0" })
      },
      facebook: {
        label: "facebook",
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`,
        icon: /* @__PURE__ */ jsx2(Facebook, { className: "h-6 w-6 shrink-0" })
      },
      email: {
        label: "email",
        url: `mailto:?subject=${encodedShareTitle}&body=${encodedShareDescription}%20${encodedShareUrl}`,
        icon: /* @__PURE__ */ jsx2(Mail, { className: "h-6 w-6 shrink-0" })
      },
      linkedIn: {
        label: "linkedin",
        url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedShareUrl}&title=${encodedShareTitle}&summary=${encodedShareDescription}`,
        icon: /* @__PURE__ */ jsx2(LinkedIn, { className: "h-6 w-6 shrink-0" })
      },
      x: {
        label: "x",
        url: `https://x.com/intent/tweet?url=${encodedShareUrl}`,
        icon: /* @__PURE__ */ jsx2(X, { className: "h-6 w-6 shrink-0" })
      }
    };
  }, [isMobile, encodedShareTitle, encodedShareUrl, encodedShareDescription]);
  const handleNativeShare = () => __async(void 0, null, function* () {
    var _a;
    if (!data.url) {
      data.url = url;
    }
    const canShare = typeof navigator !== "undefined" && navigator.canShare(data);
    if (!canShare) {
      return (_a = toast.current) == null ? void 0 : _a.publish({
        title: t("ui_common:share.failed_to_share")
      });
    }
    navigator.share(data);
  });
  const ShareButton = ({ onClick }) => {
    return (render == null ? void 0 : render({ share: onClick })) || /* @__PURE__ */ jsxs2(Button_default, { shape: "outlined", theme: "white", onClick, className, children: [
      icon,
      !hideText && t("ui_common:share.title")
    ] });
  };
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx2(Toast_default, { ref: toast }),
    isMobile ? /* @__PURE__ */ jsx2(ShareButton, { onClick: handleNativeShare }) : /* @__PURE__ */ jsx2(
      Modal_default,
      {
        closeButton: true,
        size: "2xl",
        render: ({ open }) => /* @__PURE__ */ jsx2(ShareButton, { onClick: modal ? open : handleNativeShare }),
        body: () => /* @__PURE__ */ jsxs2("div", { className: "p-L space-y-M", children: [
          /* @__PURE__ */ jsx2(Typography_default, { as: "h2", variant: "heading-L", children: heading || t("ui_common:share.title") }),
          /* @__PURE__ */ jsxs2("div", { className: "gap-M flex items-center", children: [
            poster && /* @__PURE__ */ jsx2(
              ImageWithFallback_default,
              {
                src: poster,
                width: 60,
                height: 60,
                className: "rounded-XS h-16 w-16 shrink-0 object-cover"
              }
            ),
            data.title && /* @__PURE__ */ jsx2(Typography_default, { variant: "label-M", className: "line-clamp-2", children: data.title })
          ] }),
          /* @__PURE__ */ jsxs2("ul", { className: "grid grid-cols-3 gap-4 text-start", children: [
            /* @__PURE__ */ jsx2("li", { children: /* @__PURE__ */ jsx2(
              CopyButton_default,
              {
                url,
                onError: () => {
                  var _a;
                  return (_a = toast.current) == null ? void 0 : _a.publish({
                    title: t("ui_common:share.copy_error"),
                    type: "error"
                  });
                }
              }
            ) }),
            channels.map((channel) => {
              const { label, url: url2, icon: icon2 } = shareOptions[channel];
              return /* @__PURE__ */ jsx2("li", { children: /* @__PURE__ */ jsxs2(
                Button_default,
                {
                  as: "a",
                  href: url2,
                  target: "_blank",
                  rel: "noreferrer",
                  className: "radius-XS h-12 w-full justify-start gap-3",
                  shape: "outlined",
                  theme: "white",
                  children: [
                    icon2,
                    /* @__PURE__ */ jsx2(Typography_default, { variant: "label-M", as: "span", children: t(`ui_common:share.${label}`) })
                  ]
                }
              ) }, label);
            }),
            /* @__PURE__ */ jsx2("li", { children: /* @__PURE__ */ jsxs2(
              Button_default,
              {
                className: "radius-XS h-12 w-full justify-start gap-3",
                shape: "outlined",
                theme: "white",
                onClick: () => handleNativeShare(),
                children: [
                  /* @__PURE__ */ jsx2(WorldIcon, { className: "h-6 w-6 shrink-0" }),
                  /* @__PURE__ */ jsx2(Typography_default, { variant: "label-M", as: "span", children: t("ui_common:share.other") })
                ]
              }
            ) })
          ] })
        ] })
      }
    )
  ] });
};
var Share_default = ShareModal;

export {
  Share_default
};
//# sourceMappingURL=chunk-MFVAX63R.mjs.map